/**
 * Dixon Gallery and Gardens Interactive Map
 * Initializes and configures the Leaflet map with custom markers
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { debounce } from '../modules/utilities.js';
import { MAP_CONFIG } from './config.js';
import markers from './markers.js';
import { createAllCategoryIcons } from './icons.js';
import { createCategoryLayers, createFilterControls } from './filters.js';

/**
 * Initialize the Dixon map
 */
export function initializeDixonMap() {
    const mapElement = document.getElementById('dixon-map');

    if (!mapElement) {
        console.warn('Map element #dixon-map not found');
        return null;
    }

    // Add loading class for visual feedback
    mapElement.classList.add('loading');

    // Initialize the map with configuration
    const map = L.map(mapElement, {
        ...MAP_CONFIG.mapOptions,
        center: L.latLngBounds(MAP_CONFIG.bounds).getCenter(),
    });

    // Create custom icons for each category
    const categoryIcons = createAllCategoryIcons();

    // Create category-based layer groups
    const categoryLayers = createCategoryLayers(categoryIcons, MAP_CONFIG);

    // Add all category layers to map
    Object.values(categoryLayers).forEach(layer => {
        layer.addTo(map);
    });

    // Add the map image overlay
    const imageOverlay = L.imageOverlay(MAP_CONFIG.imageUrl, MAP_CONFIG.bounds);

    // Handle image load success
    imageOverlay.on('load', () => {
        mapElement.classList.remove('loading');
        console.log('Dixon map image loaded successfully');
    });

    // Handle image load error
    imageOverlay.on('error', () => {
        mapElement.classList.remove('loading');
        mapElement.classList.add('error');
        console.error('Failed to load Dixon map image');

        // Show error message to user
        const errorMessage = document.createElement('div');
        errorMessage.className = 'map-error-message';
        errorMessage.textContent = 'Unable to load map. Please refresh the page.';
        mapElement.appendChild(errorMessage);
    });

    // Add image overlay to map
    imageOverlay.addTo(map);

    // Fit map to bounds
    map.fitBounds(MAP_CONFIG.bounds);

    // Handle window resize with debouncing for better performance
    map.on('resize', debounce(() => {
        map.fitBounds(MAP_CONFIG.bounds);
    }, MAP_CONFIG.resizeDebounce));

    // Bind filter controls to existing DOM
    createFilterControls(map, categoryLayers);

    // Expose map globally for debugging (optional, remove in production)
    if (process.env.NODE_ENV === 'development') {
        window._dixonMap = map;
        window._dixonCategoryLayers = categoryLayers;
    }

    return { map, categoryLayers, imageOverlay };
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDixonMap);
} else {
    // DOM already loaded
    initializeDixonMap();
}

export default initializeDixonMap;
